import React from "react";

import { useApolloClient, useMutation } from "@apollo/client";

import { RadioButton } from "primereact/radiobutton";
import { FileUpload, FileUploadHandlerParam } from "primereact/fileupload";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Toolbar } from "primereact/toolbar";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { Menu } from "primereact/menu";
import { ListBox } from "primereact/listbox";

/** contexts */
import { useSettings } from "../../context/settings";

/** QUERIES MUTATIONS */
import CREATE_SERVICE, { UPDATE_SERVICE } from "../../provider/Services/mutation";
import UPLOAD_FILE from "../../provider/Upload/mutation";
import { SERVICES } from "../../provider/Services/query";

/** utils */
import { getGraphsqErros, maskRealBeautify, toFloat } from "../../utilities/functions";
import { useUser } from "../../context/users";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import CATEGORYS from "../../provider/Categorys/query";

const Crud = () => {
  const [uploadFile] = useMutation(UPLOAD_FILE);
  const settings = useSettings();
  const apollo = useApolloClient();

  const user = useUser();
  const franchise_id = user?.getUser()?.franchise?.id;
  const acting_region_id = user?.getUser()?.actingRegion?.id;
  // const is_root = user?.getUser()?.is_root;

  const toast = React.useRef<any>(null);
  const dt = React.useRef<any>(null);
  const menuHourRush = React.useRef<any>(null);

  const [records, setRecords] = React.useState<any[]>([]);
  const [franchises, setFranchises] = React.useState<iFranchise[]>([]);
  const [franchise, setFranchise] = React.useState<any>(null);
  const [regions, setRegions] = React.useState<iRegion[]>([]);
  const [region, setRegion] = React.useState<any>(null);
  const [categorys, setCategorys] = React.useState<iCategory[]>([]);
  const [category, setCategory] = React.useState<any>(null);
  const [record, setRecord] = React.useState<iServices>();

  const [loading, setLoading] = React.useState<boolean>(false);
  const [load, setLoad] = React.useState(true);
  const [reload, setReload] = React.useState<boolean>(false);
  const [recordDialog, setRecordDialog] = React.useState<boolean>(false);
  const [selectedRecords, setSelectedRecords] = React.useState<any>(null);
  const [deleteRecordDialog, setDeleteRecordDialog] = React.useState<boolean>(false);
  const [deleteRecordsDialog, setDeleteRecordsDialog] = React.useState<boolean>(false);

  const [submitted, setSubmitted] = React.useState<boolean>(false);
  const [globalFilter, setGlobalFilter] = React.useState<any>(null);

  // const [rush_hour, setRush_hour] = React.useState<iRushHour[]>([])

  let emptyRecord: iServices = {
    name: "",
    description: "",
    base_rate: "0,00",
    fee: "0",
    fee_fix: false,
    rush_hours: [{ end_hour: "", start_hour: "", percent: "0,00", weekDay: 7 }],
    booking_fee: "0,00",
    capacity: "0,00",
    cost_per_km: "0,00",
    cost_per_minute: "0,00",
    icon_path: "",
    image_path: "",
    minimum_price: "0,00",
    price_per_stop: "0,00",
    price_per_people: "0,00",
    currency: 'R$',
    max_meters_next_race: 2000,
    max_meters_diff: 1500,
    max_time_diff: 300,
    is_actived: true,
    is_negotiable: true,
    is_schedulable: false,
    is_callcenter: false,
    is_service: false,
    order: 1,
  };

  const weekDays = [
    {code: 7, name: 'Todos'},
    {code: 0, name: 'Domingo'},
    {code: 1, name: 'Segunda'},
    {code: 2, name: 'Terça'},
    {code: 3, name: 'Quarta'},
    {code: 4, name: 'Quinta'},
    {code: 5, name: 'Sexta'},
    {code: 6, name: 'Sábado'},
  ]

  const upload = (e: FileUploadHandlerParam, key: string) => {
    const file = e.files;

    if (!file || file.length <= 0) return;

    uploadFile({
      variables: {
        file: file[0],
      },
    })
      .then(res => {
        let _record: any = { ...record };
        _record[key] = res.data.singleUpload;
        setRecord(_record);
      })
      .catch(error => {
        settings?.setErrorMessage(getGraphsqErros(error));
        settings?.setVisibleError(true);
      });
  };

  const loadRecords = (cleanRecord: boolean = false) => {
    setLoading(true);

    const filter: any = {
      acting_region_name: 'painel',
    };

    if (franchise_id) filter.franchise_id = Number(franchise_id);

    if (acting_region_id) filter.acting_region_id = Number(acting_region_id);

    if (franchise?.code) filter.franchise_id = Number(franchise.code);

    if (region?.code) filter.acting_region_id = Number(region.code);

    apollo
      .query({
        query: SERVICES,
        variables: filter,
        fetchPolicy: "no-cache",
      })
      .then(response => {
        const data = response.data.services;
        setFranchises(response.data.franchises);
        setRegions(response.data.regions);

        setRecords(data);
        setLoading(false);
        setLoad(false);
        setReload(false);
      })
      .catch(error => {
        console.log('Query SERVICES error: ', JSON.stringify(error));
        setRecords([]);
        settings?.setErrorMessage(getGraphsqErros(error));
        settings?.setVisibleError(true);
        setLoading(false);
        setLoad(false);
        setReload(false);
      });
  };

  const loadCategorys = () => {
    if (!franchise_id) return;

    setLoading(true);

    const variables: any = {}

    if (franchise_id) variables.franchise_id = Number(franchise_id);

    apollo
      .query({
        query: CATEGORYS,
        variables,
        fetchPolicy: "no-cache",
      })
      .then(response => {
        const data = response.data.Categorys;

        setCategorys(data);
      })
      .catch(error => {
        setCategorys([]);
        console.log('CATEGORYS', JSON.stringify(error));
        settings?.setErrorMessage(getGraphsqErros(error));
        settings?.setVisibleError(true);
      });
  }

  const openNew = () => {
    setRecord(emptyRecord);
    setSubmitted(false);
    setRecordDialog(true);
  };

  const hideDialog = () => {
    setSubmitted(false);
    setRecordDialog(false);
  };

  const hideDeleteRecordDialog = () => {
    setDeleteRecordDialog(false);
  };

  const hideDeleteRecordsDialog = () => {
    setDeleteRecordsDialog(false);
  };

  const saveRecord = () => {
    setSubmitted(true);

    if (
      !record?.name ||
      // !record.icon_path ||
      // !record.image_path ||
      !record?.base_rate ||
      !record?.fee ||
      !record?.minimum_price ||
      !record?.cost_per_minute ||
      !record?.cost_per_km ||
      !record?.booking_fee ||
      !record?.rush_hours
    ) {
      return;
    }

    const _record: iServices = { ...record };

    _record.fee = toFloat(_record.fee);
    _record.order = toFloat(_record.order);
    _record.capacity = toFloat(_record.capacity);
    _record.base_rate = toFloat(_record.base_rate);
    _record.booking_fee = toFloat(_record.booking_fee);
    _record.cost_per_km = toFloat(_record.cost_per_km);
    _record.minimum_price = toFloat(_record.minimum_price);
    _record.price_per_stop = toFloat(_record.price_per_stop);
    _record.price_per_people = toFloat(_record.price_per_people);
    _record.cost_per_minute = toFloat(_record.cost_per_minute);
    _record.rush_hour = { data: _record.rush_hours.filter(i => i.end_hour && i.start_hour && i.percent).map(i => ({ ...i, percent: toFloat(i.percent) })) };

    _record.boarding_waiting_time_in_seconds = parseInt(`${_record.boarding_waiting_time_in_seconds}`);
    _record.max_meters_request_drivers = parseInt(`${_record.max_meters_request_drivers}`);
    _record.max_meters_next_race = parseInt(`${_record.max_meters_next_race}`);
    _record.max_meters_diff = parseInt(`${_record.max_meters_diff}`);
    _record.max_time_diff = parseInt(`${_record.max_time_diff}`);
    _record.max_search_booking_in_seconds = parseInt(`${_record.max_search_booking_in_seconds}`);
    _record.franchise_id = franchise?.code ? parseInt(franchise.code) : undefined;

    if (franchise_id) {
      _record.franchise_id = Number(franchise_id);
    }

    if (acting_region_id) _record.acting_region_id = Number(acting_region_id);

    if (region) {
      _record.acting_region_id = Number(region.code);
    }

    if (category) 
      _record.category_id = Number(category.code);

    delete _record.__typename;
    delete _record.franchise;
    delete _record.actingRegion;
    delete _record.category;
    // @ts-expect-error
    delete _record.rush_hours;

    setLoading(true);
    let mutation = CREATE_SERVICE;

    if (record?.id) {
      _record.id = parseInt(`${_record.id}`);
      mutation = UPDATE_SERVICE;
    }

    apollo
      .mutate({
        mutation,
        variables: {
          data: {
            ..._record,
          },
        },
      })
      .then(response => {
        setLoading(false);

        toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro salvo com sucesso.", life: 3000 });

        loadRecords();
        setRecordDialog(false);
        setRecord(emptyRecord);
        setFranchise(null);
        setRegion(null);
      })
      .catch(error => {
        console.log('UPDATE Service error:', JSON.stringify(error));
        setLoading(false);
        settings.setErrorMessage(getGraphsqErros(error));
        settings.setVisibleError(true);
      });
  };

  const editRecord = (record: iServices) => {
    const _record: iServices = { ...record };

    _record.order = record.order;
    _record.capacity = record.capacity;
    _record.fee = maskRealBeautify(record.fee, true);
    _record.base_rate = maskRealBeautify(record.base_rate, true);
    _record.booking_fee = maskRealBeautify(record.booking_fee, true);
    _record.cost_per_km = maskRealBeautify(record.cost_per_km, true);
    _record.minimum_price = maskRealBeautify(record.minimum_price, true);
    _record.price_per_stop = maskRealBeautify(record.price_per_stop, true);
    _record.price_per_people = maskRealBeautify(record.price_per_people, true);
    _record.cost_per_minute = maskRealBeautify(record.cost_per_minute, true);

    _record.boarding_waiting_time_in_seconds = parseInt(`${record.boarding_waiting_time_in_seconds}`);
    _record.max_meters_request_drivers = parseInt(`${record.max_meters_request_drivers}`);
    _record.max_meters_next_race = parseInt(`${record.max_meters_next_race}`);
    _record.max_meters_diff = parseInt(`${record.max_meters_diff}`);
    _record.max_time_diff = parseInt(`${record.max_time_diff}`);
    _record.max_search_booking_in_seconds = parseInt(`${record.max_search_booking_in_seconds}`);

    if (_record.rush_hour && _record.rush_hour.data && Array.isArray(_record.rush_hour.data)) {
      _record.rush_hours = _record.rush_hour.data.map((i: iRushHour) => ({ ...i, percent: maskRealBeautify(i.percent, true) }));
    } else {
      _record.rush_hours = [{ end_hour: "", start_hour: "", percent: "0,00", weekDay: 7 }];
    }

    setFranchise({ code: _record.franchise?.id, name: _record.franchise?.name });
    setRegion({ code: _record.actingRegion?.id, name: _record.actingRegion?.name });
    setRecord({ ..._record });
    setRecordDialog(true);
  };

  const confirmDeleteRecord = (record: any) => {
    setRecord(record);
    setDeleteRecordDialog(true);
  };

  const deleteRecord = () => {
    apollo
      .mutate({
        mutation: UPDATE_SERVICE,
        variables: {
          data: {
            id: parseInt(`${record?.id}`),
            is_deleted: true,
          },
        },
      })
      .then(response => {
        let _records = records.filter((val: any) => val.id !== record?.id);
        setRecords(_records);
        setDeleteRecordDialog(false);
        setRecord(emptyRecord);
        toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro deletado", life: 3000 });
      })
      .catch(error => {
        console.log('UPDATE_SERVICE delete error: ', JSON.stringify(error));
        setLoading(false);
        settings.setErrorMessage(getGraphsqErros(error));
        settings.setVisibleError(true);
      });
  };

  const confirmDeleteSelected = () => {
    setDeleteRecordsDialog(true);
  };

  const deleteSelectedRecords = () => {
    let _records = records.filter((val: any) => !selectedRecords.includes(val));
    setRecords(_records);
    setDeleteRecordsDialog(false);
    setSelectedRecords(null);
    toast.current.show({ severity: "success", summary: "Successful", detail: "Records Deleted", life: 3000 });
  };

  const onRoleChange = (e: any) => {
    let _record: any = { ...record };
    _record.is_actived = e.value;
    setRecord(_record);
  };

  const onFeeFixChange = (e: any) => {
    let _record: any = { ...record };
    _record.fee_fix = e.value;
    setRecord(_record);
  };

  const onPushChange = (e: any) => {
    let _record: any = { ...record };
    _record.send_notification = e.value;
    setRecord(_record);
  };

  const onChangeGender = (e: any) => {
    let _record: any = { ...record };
    _record.gender = e;
    setRecord(_record);
  }

  const onInputChange = (e: any, name: string) => {
    let val = (e.target && e.target.value) || "";

    if (
      name === 'is_negotiable' ||
      name === 'single_race' ||
      name === 'is_schedulable' ||
      name === 'is_callcenter' ||
      name === 'is_service'
    ) val = e.value;

    if (
      name === "fee" ||
      name === "base_rate" ||
      name === "booking_fee" ||
      name === "cost_per_km" ||
      name === "cost_per_minute" ||
      name === "minimum_price" ||
      name === "price_per_stop" ||
      name === "price_per_people"
    ) {
      val = maskRealBeautify(val);
    }

    let _record: any = { ...record };
    _record[name] = val;

    if ((name === 'is_negotiable' || name === 'is_schedulable') && val === true) {

      if (name === 'is_negotiable') _record['is_schedulable'] = false;

      if (name === 'is_schedulable') _record['is_negotiable'] = false;
    }

    setRecord(_record);
  };

  const onRushHourChange = (e: any, name: string, index: number) => {
    let val = (e.target && e.target.value) || "";

    if (name === "weekDay") val = e;

    let _record: any = { ...record };
    if (Array.isArray(_record.rush_hours)) {
      if (name === "percent") {
        _record.rush_hours[index][name] = maskRealBeautify(val);
      } else {
        _record.rush_hours[index][name] = val;
      }
    }

    setRecord(_record);
  };

  const onRushHourNew = () => {
    let _record: any = { ...record };

    _record.rush_hours.push({ end_hour: "", start_hour: "", percent: "0,00", weekDay: 7 });

    setRecord(_record);
  };

  const onRushHourRemove = (index: number) => {
    let _record: any = { ...record };

    _record.rush_hours = _record.rush_hours.filter((item: any, i: any) => i !== index);

    setRecord(_record);
  };

  const leftToolbarTemplate = () => {
    return (
      <React.Fragment>
        <Button label="Novo" icon="pi pi-plus" className="p-button-success mr-2 " onClick={openNew} />
        {/* <Button
          label="Delete"
          icon="pi pi-trash"
          className="p-button-danger mb-2"
          onClick={confirmDeleteSelected}
          disabled={!selectedRecords || !selectedRecords.length}
        /> */}
      </React.Fragment>
    );
  };

  const imagesBodyTemplate = (rowData: iServices) => {
    return (
      <>
        <img src={rowData.icon_path} alt={rowData.name} className="product-image service-image mr-2" />
        <img src={rowData.image_path} alt={rowData.name} className="product-image service-image" />
      </>
    );
  };

  const codeBodyTemplate = (rowData: iServices) => {
    return (
      <>
        <span className="p-column-title">Cód.</span>
        {rowData.id}
      </>
    );
  };

  const nameBodyTemplate = (rowData: iServices) => {
    return (
      <>
        <span className="p-column-title">Nome</span>
        {rowData.name}
      </>
    );
  };

  const franchiseBodyTemplate = (rowData: iServices) => {
    return (
      <>
        <span className="p-column-title">Franquia</span>
        {rowData.franchise?.name}
      </>
    );
  };

  const actingRegionBodyTemplate = (rowData: iUser) => {
    return (
      <>
        <span className="p-column-title">Região</span>
        {rowData.actingRegion?.name}
      </>
    );
  };

  const baseRateBodyTemplate = (rowData: iServices) => {
    return (
      <>
        <span className="p-column-title">Tarifa base</span>
        {maskRealBeautify(rowData.base_rate, true, "R$")}
      </>
    );
  };

  const feeBodyTemplate = (rowData: iServices) => {
    return (
      <>
        <span className="p-column-title">Taxa</span>
        {maskRealBeautify(rowData.fee, true, "")}%
      </>
    );
  };

  const descriptionBodyTemplate = (rowData: iServices) => {
    return (
      <>
        <span className="p-column-title">Descrição</span>
        {rowData.description ?? "-"}
      </>
    );
  };

  const statusBodyTemplate = (rowData: iServices) => {
    return (
      <>
        <span className="p-column-title">Situação</span>
        <span className={`product-badge status-${!rowData.is_actived ? "disabled" : "actived"}`}>{rowData.is_actived ? "Ativo" : "Inativo"}</span>
      </>
    );
  };

  const actionBodyTemplate = (rowData: iServices) => {
    return (
      <div className="actions">
        <Button icon="pi pi-pencil" className="p-button-rounded p-button-info mr-2" onClick={() => editRecord(rowData)} />
        <Button icon="pi pi-trash" className="p-button-rounded p-button-danger mt-0" onClick={() => confirmDeleteRecord(rowData)} />
      </div>
    );
  };

  const header = (
    <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
      <h5 className="m-0">{loading ? "Carregando serviços..." : "Serviços"}</h5>
      <span className="block mt-2 md:mt-0 p-input-icon-left">
        <i className="pi pi-search" />
        <InputText className="min-w-full" type="search" onInput={e => setGlobalFilter(e.currentTarget.value)} placeholder="Procurar..." />
      </span>
    </div>
  );

  const recordDialogFooter = (
    <>
      <Button label="Cancelar" icon="pi pi-times" disabled={loading} className="p-button-text text-red-500" onClick={hideDialog} />
      <Button label={loading ? "Salvando..." : "Salvar"} disabled={loading} icon="pi pi-check" className="p-button-text" onClick={saveRecord} />
    </>
  );

  const deleteRecordDialogFooter = (
    <>
      <Button label="Não" icon="pi pi-times" className="p-button-text text-red-500" onClick={hideDeleteRecordDialog} />
      <Button label="Sim, excluir!" icon="pi pi-check" className="p-button-text" onClick={deleteRecord} />
    </>
  );

  const deleteRecordsDialogFooter = (
    <>
      <Button label="Não" icon="pi pi-times" className="p-button-text" onClick={hideDeleteRecordsDialog} />
      <Button label="Sim, excluir!" icon="pi pi-check" className="p-button-text" onClick={deleteSelectedRecords} />
    </>
  );

  React.useEffect(() => {
    loadRecords(true);
  }, [region?.code]);

  React.useEffect(() => {
    if (franchise?.code){
      setRegion(null);
      setReload(true);
      loadRecords(true);
    }
  }, [franchise?.code]);

  React.useEffect(() => {
    loadCategorys();
  }, [franchise_id])

  return (
    <div className="grid crud-demo">
      {franchise_id && acting_region_id ? null : (
        load ? (
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
        ) : (
          <>
            <div className="card">
              <div className="flex flex-column md:flex-row flex-wrap">
                { !franchise_id ? (
                  <div className="flex flex-column md:flex-row flex-wrap">
                    <label htmlFor="name">Franquia</label>
                    <div className="field col-12 md:col-12">
                      <Dropdown
                        value={franchise}
                        onChange={e => setFranchise(e.value)}
                        options={franchises.map(i => ({ code: i.id, name: i.name }))}
                        optionLabel="name"
                        placeholder="Selecione uma Franquia"
                      />
                    </div>
                  </div>
                ) : null }
  
  
                { !acting_region_id ? (
                  <div className="flex flex-column md:flex-row flex-wrap">
                    <label htmlFor="name">Região</label>
                    <div className="field col-12 md:col-12">
                      {reload ? (
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
                      ) : (
                        <Dropdown
                          value={region}
                          onChange={e => setRegion(e.value)}
                          options={regions.map(i => ({ code: i.id, name: i.name }))}
                          optionLabel="name"
                          placeholder="Selecione uma Região"
                        />
                      )}
                    </div>
                  </div>
                ) : null }
              </div>
            </div>
          </>
        )
      )}
      <div className="col-12">
        <div className="card">
          <Toast ref={toast} />
          <Toolbar className="mb-4" left={leftToolbarTemplate}></Toolbar>

          <DataTable
            ref={dt}
            value={records}
            dataKey="id"
            paginator
            stripedRows={true}
            rowHover
            showGridlines
            rows={10}
            rowsPerPageOptions={[5, 10, 25]}
            className="datatable-responsive"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
            globalFilter={globalFilter}
            emptyMessage="Nenhum registro encontrado"
            header={header}
            responsiveLayout="scroll">
            <Column field="image_path" header="Icone/Imagem" body={imagesBodyTemplate} headerStyle={{}} />
            <Column field="order" header="Ordem" sortable body={codeBodyTemplate} headerStyle={{}} />
            <Column field="name" header="Nome" sortable body={nameBodyTemplate} headerStyle={{}} />
            { !franchise_id ? (
              <Column field="franchise.name" header="Franquia" sortable body={franchiseBodyTemplate} headerStyle={{}} />
            ) : null }
            { !acting_region_id ? (
              <Column field="actingRegion.name" header="Região" sortable body={actingRegionBodyTemplate} headerStyle={{}} />
            ) : null }
            <Column field="description" header="Descricao" body={descriptionBodyTemplate} sortable headerStyle={{}} />
            <Column field="base_rate" header="Tarifa base" body={baseRateBodyTemplate} sortable headerStyle={{}} />
            <Column field="fee" header="Taxa" body={feeBodyTemplate} sortable headerStyle={{}} />

            <Column field="is_actived" header="Situação" body={statusBodyTemplate} sortable headerStyle={{}} />
            <Column header="Ações" className="flex flex-row align-items-center justify-content-end" body={actionBodyTemplate} />
          </DataTable>

          {/* Modal adicionar editar */}
          <Dialog
            visible={recordDialog}
            style={{ width: "550px" }}
            header={record?.id ? "Detalhes do Serviço" : "Novo Serviço"}
            modal
            className="p-fluid"
            footer={recordDialogFooter}
            onHide={hideDialog}>
            {record?.image_path && record.icon_path ? (
              <div className="flex flex-column md:flex-row">
                <img
                  src={record?.icon_path}
                  alt={record?.name}
                  width="100"
                  height={100}
                  style={{ objectFit: "contain" }}
                  className="mt-0 mx-auto mb-5 block shadow-2"
                />
                <img
                  src={record?.image_path}
                  alt={record?.name}
                  width="100"
                  height={100}
                  style={{ objectFit: "contain" }}
                  className="mt-0 mx-auto mb-5 block shadow-2"
                />
              </div>
            ) : null}

            <div className="flex flex-column md:flex-row flex-wrap">
              <div className="field col-12 md:col-12">
                <label htmlFor="name">* Nome</label>
                <InputText
                  id="name"
                  value={record?.name}
                  onChange={e => onInputChange(e, "name")}
                  required
                  autoFocus
                  className={classNames("p-3", { "p-invalid": submitted && !record?.name })}
                />
                {submitted && !record?.name && <small className="p-invalid">Nome é obrigatório.</small>}
              </div>

              { !franchise_id ? (
                <div className="flex flex-column md:flex-row flex-wrap">
                  <div className="field col-12 md:col-12">
                    <label htmlFor="name">Franquia</label>
                    <Dropdown
                      value={franchise}
                      onChange={e => setFranchise(e.value)}
                      options={franchises.map(i => ({ code: i.id, name: i.name }))}
                      optionLabel="name"
                      placeholder="Selecione uma Franquia"
                    />
                  </div>
                </div>
              ) : null }

              { !acting_region_id ? (
                <div className="flex flex-column md:flex-row flex-wrap">
                  <div className="field col-12 md:col-12">
                    <label htmlFor="name">Região</label>
                    <Dropdown
                      value={region}
                      onChange={e => setRegion(e.value)}
                      options={regions.map(i => ({ code: i.id, name: i.name }))}
                      optionLabel="name"
                      placeholder="Selecione uma Região"
                    />
                  </div>
                </div>
              ) : null }

              <div className="field col-12 md:col-12">
                <label htmlFor="description">* Descrição</label>
                <InputText
                  id="description"
                  value={record?.description}
                  onChange={e => onInputChange(e, "description")}
                  required
                  className={classNames("p-3", { "p-invalid": submitted && !record?.description })}
                />
                {submitted && !record?.description && <small className="p-invalid">Descrição é obrigatório.</small>}
              </div>

              { franchise_id ? (
                <div className="flex flex-column md:flex-row flex-wrap">
                  <div className="field col-12 md:col-12">
                    <label htmlFor="name">Categoria</label>
                    <Dropdown
                      value={category}
                      onChange={e => setCategory(e.value)}
                      options={categorys.map(i => ({ code: i.id, name: i.name }))}
                      optionLabel="name"
                      placeholder="Selecione uma Categoria"
                    />
                  </div>
                </div>
              ) : null }

            </div>
            <div className="flex flex-column md:flex-row flex-wrap ">
              <div className="field col-12 md:col-12">
                <div className="field col-12 md:col-12">
                  <label htmlFor="is_negotiable">Tarifa negociável</label>
                  <InputSwitch
                    checked={record?.is_negotiable}
                    onChange={e => onInputChange(e, "is_negotiable")}
                    style={{
                      marginLeft: 25,
                      verticalAlign: 'middle',
                    }}
                  />
                </div>

                <div className="field col-12 md:col-12">
                  <label htmlFor="is_schedulable">Permite agendamento?</label>
                  <InputSwitch
                    checked={record?.is_schedulable}
                    onChange={e => onInputChange(e, "is_schedulable")}
                    style={{
                      marginLeft: 25,
                      verticalAlign: 'middle',
                    }}
                  />
                </div>
              </div>

              <div className="field col-12 md:col-12">
                <div className="field col-12 md:col-12">
                  <label htmlFor="is_callcenter">Central de Chamadas?</label>

                  <InputSwitch
                    checked={record?.is_callcenter}
                    onChange={e => onInputChange(e, "is_callcenter")}
                    style={{
                      marginLeft: 25,
                      verticalAlign: 'middle',
                    }}
                  />
                </div>

                <div className="field col-12 md:col-12">
                  <label htmlFor="is_service">Serviços Gerais?</label>
                  
                  <InputSwitch
                    checked={record?.is_service}
                    onChange={e => onInputChange(e, "is_service")}
                    style={{
                      marginLeft: 25,
                      verticalAlign: 'middle',
                    }}
                  />
                </div>

                <div className="field col-12 md:col-12">
                  <label htmlFor="single_race">Permite corrida avulsa?</label>
                  <InputSwitch
                    checked={record?.single_race}
                    onChange={e => onInputChange(e, "single_race")}
                    style={{
                      marginLeft: 25,
                      verticalAlign: 'middle',
                    }}
                  />
                </div>
              </div>

              <div className="field col-12 md:col-4">
                <label htmlFor="capacity">* Capacidade</label>
                <InputText
                  id="capacity"
                  type={"number"}
                  value={record?.capacity}
                  autoComplete={"new-password"}
                  onChange={e => onInputChange(e, "capacity")}
                  required
                  className={classNames("p-3", { "p-invalid": submitted && !record?.capacity })}
                />
                {submitted && record?.capacity === "" && <small className="p-invalid">Capacidade é obrigatório.</small>}
              </div>

              <div className="field col-12 md:col-4">
                <label htmlFor="minimum_price">* Cobrança mínima</label>
                <InputText
                  id="minimum_price"
                  type={"text"}
                  autoComplete={"new-password"}
                  value={record?.minimum_price}
                  onChange={e => onInputChange(e, "minimum_price")}
                  required
                  className={classNames("p-3", { "p-invalid": submitted && !record?.minimum_price })}
                />
                {submitted && !record?.minimum_price && <small className="p-invalid">Preço mínimo é obrigatório.</small>}
              </div>

              <div className="field col-12 md:col-4">
                <label htmlFor="base_rate">* Tarifa base</label>
                <InputText
                  id="base_rate"
                  type={"text"}
                  autoComplete={"new-password"}
                  value={record?.base_rate}
                  onChange={e => onInputChange(e, "base_rate")}
                  required
                  className={classNames("p-3", { "p-invalid": submitted && !record?.base_rate })}
                />
                {submitted && !record?.base_rate && <small className="p-invalid">Tarifa base é obrigatório.</small>}
              </div>
            </div>

            <div className="flex flex-column md:flex-row flex-wrap ">
              <div className="field col-12 md:col-4">
                <label htmlFor="cost_per_minute">* Custo por minuto</label>
                <InputText
                  id="cost_per_minute"
                  type={"text"}
                  value={record?.cost_per_minute}
                  autoComplete={"new-password"}
                  onChange={e => onInputChange(e, "cost_per_minute")}
                  required
                  className={classNames("p-3", { "p-invalid": submitted && !record?.cost_per_minute })}
                />
                {submitted && !record?.cost_per_minute && <small className="p-invalid">Custo por minuto é obrigatório.</small>}
              </div>

              <div className="field col-12 md:col-4">
                <label htmlFor="cost_per_km">* Custo por KM</label>
                <InputText
                  id="cost_per_km"
                  type={"text"}
                  value={record?.cost_per_km}
                  autoComplete={"new-password"}
                  onChange={e => onInputChange(e, "cost_per_km")}
                  required
                  className={classNames("p-3", { "p-invalid": submitted && !record?.cost_per_km })}
                />
                {submitted && !record?.cost_per_km && <small className="p-invalid">Custo por KM é obrigatório.</small>}
              </div>

              <div className="field col-12 md:col-4">
                <label htmlFor="price_per_stop">Custo por parada adicional</label>
                <InputText
                  id="price_per_stop"
                  type={"text"}
                  value={record?.price_per_stop}
                  autoComplete={"new-password"}
                  onChange={e => onInputChange(e, "price_per_stop")}
                  className={classNames("p-3", {})}
                />
              </div>

            </div>
            <div className="flex flex-column md:flex-row flex-wrap ">

              <div className="field col-12 md:col-4">
                <label htmlFor="booking_fee">* Custo adicionais</label>
                <InputText
                  id="booking_fee"
                  type={"text"}
                  value={record?.booking_fee}
                  autoComplete={"new-password"}
                  onChange={e => onInputChange(e, "booking_fee")}
                  required
                  className={classNames("p-3", {})}
                />
              </div>

              <div className="field col-12 md:col-4">
                <label htmlFor="price_per_people">Custo por pessoa</label>
                <InputText
                  id="price_per_people"
                  type={"text"}
                  value={record?.price_per_people}
                  autoComplete={"new-password"}
                  onChange={e => onInputChange(e, "price_per_people")}
                  className={classNames("p-3", {})}
                />
              </div>

              <div className="field col-12 md:col-4">
                <label htmlFor="currency">Moeda</label>
                <InputText
                  id="currency"
                  value={record?.currency}
                  onChange={e => onInputChange(e, "currency")}
                  className={classNames("p-3", {})}
                />
              </div>

            </div>

            <hr />

            <div className="flex flex-column md:flex-row flex-wrap ">
              <div className="field col-12 md:col-12">
                <label className="mb-3">* Icone do serviço</label>

                <FileUpload
                  chooseLabel="Procurar"
                  auto
                  name="file"
                  customUpload={true}
                  uploadHandler={e => upload(e, "icon_path")}
                  accept="image/*"
                  maxFileSize={1000000}
                />
              </div>

              <div className="field col-12 md:col-12">
                <label className="mb-3">* Imagem do serviço</label>

                <FileUpload
                  chooseLabel="Procurar"
                  auto
                  name="file"
                  customUpload={true}
                  uploadHandler={e => upload(e, "image_path")}
                  accept="image/*"
                  maxFileSize={1000000}
                />
              </div>
            </div>

            <div className="flex flex-column md:flex-row flex-wrap ">
              <div className="field col-12 md:col-12">
                <div className="flex align-items-center justify-content-between mb-0 p-0 pb-0 mt-4">
                  <h5 className="m-0">Horários de pico </h5>
                  <Button icon="pi pi-bars" className="p-button-text" onClick={event => menuHourRush.current.toggle(event)} />
                  <Menu
                    ref={menuHourRush}
                    popup
                    model={[
                      {
                        label: "Novo",
                        icon: "pi pi-fw pi-plus",
                        command(e) {
                          onRushHourNew();
                        },
                      },
                    ]}
                  />
                </div>

                {record?.rush_hours?.map((item, index) => (
                  <>
                    <div className="flex align-items-center justify-content-between mb-0 p-0 pb-0 mt-4">
                      <label className="mb-3">
                        {item.start_hour.length > 0 ? item.start_hour : "00:00"} à {item.end_hour.length > 0 ? item.end_hour : "00:00"}
                      </label>
                      <Button icon="pi pi-fw pi-minus" className="p-button-text" onClick={() => onRushHourRemove(index)} />
                    </div>
                    <div className="flex flex-column md:flex-row flex-wrap ">
                      <div className="field col-12 md:col-4">
                        <label htmlFor="start_hour">* Horário de início</label>
                        <InputText
                          id="start_hour"
                          type={"time"}
                          value={record.rush_hours[index].start_hour}
                          autoComplete={"new-password"}
                          onChange={e => onRushHourChange(e, "start_hour", index)}
                          className={classNames("p-3", {})}
                        />
                      </div>

                      <div className="field col-12 md:col-4">
                        <label htmlFor="end_hour">* Horário final</label>
                        <InputText
                          id="end_hour"
                          type={"time"}
                          value={record.rush_hours[index].end_hour}
                          autoComplete={"new-password"}
                          onChange={e => onRushHourChange(e, "end_hour", index)}
                          className={classNames("p-3", {})}
                        />
                      </div>

                      <div className="field col-12 md:col-4">
                        <label htmlFor="percent">* Porcentagem</label>
                        <InputText
                          id="percent"
                          type={"text"}
                          value={record.rush_hours[index].percent}
                          autoComplete={"new-password"}
                          onChange={e => onRushHourChange(e, "percent", index)}
                          className={classNames("", { "p-invalid": submitted && !record?.cost_per_minute })}
                          style={{ padding: "1.16rem" }}
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="name">Dia da semana</label>
                      <div className="field col-12 md:col-4">
                        <Dropdown
                          value={weekDays.find((week) => week.code === record.rush_hours[index].weekDay) || {code: 7, name: 'Todos'}}
                          onChange={e => onRushHourChange(e.value.code, "weekDay", index)}
                          options={weekDays}
                          optionLabel="name"
                          placeholder="Selecione um dia da semana"
                        />
                      </div>
                    </div>
                  </>
                ))}
              </div>
            </div>

            <hr />

            <div className="card">
              <div className="flex flex-column md:flex-row flex-wrap ">
                <h6>Parâmetros</h6>
                <div className="field col-12 md:col-12">
                  <label htmlFor="order">* Ordem de exibição</label>
                  <InputText
                    id="order"
                    type={"number"}
                    value={record?.order}
                    autoComplete={"new-password"}
                    onChange={e => onInputChange(e, "order")}
                    required
                    step={1}
                    min={0}
                    className={classNames("p-3", { "p-invalid": submitted && !record?.boarding_waiting_time_in_seconds })}
                  />
                  {submitted && !record?.order && <small className="p-invalid">Informe a ordem de exibição</small>}
                </div>
                <div className="field col-12 md:col-12">
                  <label htmlFor="boarding_waiting_time_in_seconds">* Aguardar embarque por (Segundos)</label>
                  <InputText
                    id="boarding_waiting_time_in_seconds"
                    type={"number"}
                    value={record?.boarding_waiting_time_in_seconds}
                    autoComplete={"new-password"}
                    onChange={e => onInputChange(e, "boarding_waiting_time_in_seconds")}
                    required
                    step={1}
                    min={0}
                    className={classNames("p-3", { "p-invalid": submitted && !record?.boarding_waiting_time_in_seconds })}
                  />
                  {submitted && !record?.boarding_waiting_time_in_seconds && <small className="p-invalid">Informe o tempo de espera.</small>}
                </div>
                <div className="field col-12 md:col-12">
                  <label htmlFor="max_meters_request_drivers">* Procurar Motorista no Raio de (Metros)</label>
                  <InputText
                    id="max_meters_request_drivers"
                    type={"number"}
                    value={record?.max_meters_request_drivers}
                    autoComplete={"new-password"}
                    onChange={e => onInputChange(e, "max_meters_request_drivers")}
                    required
                    step={1}
                    min={0}
                    className={classNames("p-3", { "p-invalid": submitted && !record?.max_meters_request_drivers })}
                  />
                  {submitted && !record?.max_meters_request_drivers && <small className="p-invalid">Informe o raio de busca.</small>}
                </div>

                <div className="field col-12 md:col-12">
                  <label htmlFor="max_meters_next_race">Corrida em Sequencia no Raio de (Metros)</label>
                  <InputText
                    id="max_meters_next_race"
                    type={"number"}
                    value={record?.max_meters_next_race}
                    autoComplete={"new-password"}
                    onChange={e => onInputChange(e, "max_meters_next_race")}
                    step={1}
                    min={0}
                    className={classNames("p-3", { "p-invalid": submitted && !record?.max_meters_next_race })}
                  />
                </div>

                <div className="field col-12 md:col-12">
                  <label htmlFor="max_meters_diff">Diferença máxima para recalculo (Metros)</label>
                  <InputText
                    id="max_meters_diff"
                    type={"number"}
                    value={record?.max_meters_diff}
                    autoComplete={"new-password"}
                    onChange={e => onInputChange(e, "max_meters_diff")}
                    step={1}
                    min={0}
                    className={classNames("p-3", { "p-invalid": submitted && !record?.max_meters_diff })}
                  />
                  <small className="p-invalid">Se a diferença entre a distancia real percorrida e a distancia estimada inicial for maior que este valor, o preço da corrida será recalculado.</small>
                </div>

                <div className="field col-12 md:col-12">
                  <label htmlFor="max_time_diff">Diferença máxima para recalculo (Segundos)</label>
                  <InputText
                    id="max_time_diff"
                    type={"number"}
                    value={record?.max_time_diff}
                    autoComplete={"new-password"}
                    onChange={e => onInputChange(e, "max_time_diff")}
                    step={1}
                    min={0}
                    className={classNames("p-3", { "p-invalid": submitted && !record?.max_time_diff })}
                  />
                  <small className="p-invalid">Se a diferença entre o tempo real da corrida e o tempo estimado inicial for maior que este valor, o preço da corrida será recalculado.</small>
                </div>

                <div className="field col-12 md:col-12">
                  <label htmlFor="max_search_booking_in_seconds">* Ficar procurando motostista por (Segundos)</label>
                  <InputText
                    id="max_search_booking_in_seconds"
                    type={"number"}
                    value={record?.max_search_booking_in_seconds}
                    autoComplete={"new-password"}
                    onChange={e => onInputChange(e, "max_search_booking_in_seconds")}
                    required
                    step={1}
                    min={0}
                    className={classNames("p-3", { "p-invalid": submitted && !record?.max_search_booking_in_seconds })}
                  />
                  {submitted && !record?.max_search_booking_in_seconds && <small className="p-invalid">Informe o tempo de procura.</small>}
                </div>
                <div className="field col-12 md:col-12">
                  <label className="mb-3">* Envia Notificação PUSH de novas corridas?</label>
                  <div className="formgrid grid">
                    <div className="field-radiobutton col-4">
                      <RadioButton
                        inputId="push1"
                        name="send_notification"
                        value={true}
                        onChange={onPushChange}
                        checked={record?.send_notification}
                      />
                      <label htmlFor="push1">Sim</label>
                    </div>
                    <div className="field-radiobutton col-4">
                      <RadioButton
                        inputId="push2"
                        name="send_notification"
                        value={false}
                        onChange={onPushChange}
                        checked={!record?.send_notification}
                      />
                      <label htmlFor="push2">Não</label>
                    </div>
                  </div>
                </div>

                <div className="field col-12 md:col-12">
                  <label className="mb-3">Gênero</label>
                  <div className="formgrid grid">
                    <div className="field-radiobutton col-4">
                      <RadioButton
                        inputId="push1"
                        name="send_notification"
                        value={true}
                        onChange={() => onChangeGender(null)}
                        checked={!record?.gender || record.gender === 'NONE'}
                      />
                      <label htmlFor="push1">Todos</label>
                    </div>

                    <div className="field-radiobutton col-4">
                      <RadioButton
                        inputId="push1"
                        name="send_notification"
                        value={true}
                        onChange={() => onChangeGender('FEMALE')}
                        checked={record?.gender === 'FEMALE'}
                      />
                      <label htmlFor="push1">Mulheres</label>
                    </div>

                    <div className="field-radiobutton col-4">
                      <RadioButton
                        inputId="push2"
                        name="send_notification"
                        value={false}
                        onChange={() => onChangeGender('MALE')}
                        checked={record?.gender === 'MALE'}
                      />
                      <label htmlFor="push2">Homens</label>
                    </div>
                  </div>
                </div>
              </div>
              <hr />
            </div>
            <div className="card">
              <div className="flex flex-column md:flex-row flex-wrap ">
                <h6>Repasse</h6>
                <div className="field col-12 md:col-12">
                  <label htmlFor="fee">* Taxa administrativa</label>
                  <InputText
                    id="fee"
                    type={"text"}
                    value={record?.fee}
                    autoComplete={"new-password"}
                    onChange={e => onInputChange(e, "fee")}
                    required
                    className={classNames("p-3", { "p-invalid": submitted && !record?.fee })}
                  />
                  {submitted && !record?.fee && <small className="p-invalid">Informe a taxa.</small>}
                </div>
              </div>
              <div className="field col-12 md:col-12">
                <label className="mb-3">Tipo de taxa</label>
                <div className="formgrid grid">
                  <div className="field-radiobutton col-4">
                    <RadioButton inputId="role1" name="is_actived" value={false} onChange={onFeeFixChange} checked={!record?.fee_fix} />
                    <label htmlFor="role1">Porcentagem</label>
                  </div>
                  <div className="field-radiobutton col-4">
                    <RadioButton inputId="role2" name="is_actived" value={true} onChange={onFeeFixChange} checked={record?.fee_fix} />
                    <label htmlFor="role2">Fixa</label>
                  </div>
                </div>
              </div>
            </div>

            <hr />
            <div className="flex flex-column md:flex-row ">
              <div className="field col-12 md:col-12">
                <label className="mb-3">* Situação</label>
                <div className="formgrid grid">
                  <div className="field-radiobutton col-4">
                    <RadioButton inputId="role1" name="is_actived" value={true} onChange={onRoleChange} checked={record?.is_actived} />
                    <label htmlFor="role1">Ativo</label>
                  </div>
                  <div className="field-radiobutton col-4">
                    <RadioButton inputId="role2" name="is_actived" value={false} onChange={onRoleChange} checked={!record?.is_actived} />
                    <label htmlFor="role2">Inativo</label>
                  </div>
                </div>
              </div>
            </div>
          </Dialog>

          {/* Modal remover registro unico */}
          <Dialog
            visible={deleteRecordDialog}
            // style={{ width: "450px" }}
            header="Atenção"
            modal
            footer={deleteRecordDialogFooter}
            onHide={hideDeleteRecordDialog}>
            <div className="flex align-items-center justify-content-center">
              <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
              {record && (
                <span>
                  Tem certeza de que deseja excluir <b>{record?.name}</b>?
                </span>
              )}
            </div>
          </Dialog>

          {/* Modal remover multiplos registros */}
          <Dialog
            visible={deleteRecordsDialog}
            // style={{ width: "450px" }}
            header="Atenção"
            modal
            footer={deleteRecordsDialogFooter}
            onHide={hideDeleteRecordsDialog}>
            <div className="flex align-items-center justify-content-center">
              <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
              {record && <span>Tem certeza de que deseja excluir os registros selecionados?</span>}
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default Crud;
